<template>
  <div class="card p-shadow-app">
    <h3>PATs por Status</h3>
    <Toast />
    <div class="p-grid p-fluid dashboard p-jc-center p-mb-3">
      <W4Mstreak
        :states="streakIndicators"
        :loader="loadingIndicators"
        @W4MStreakChangeSelected="onPATClick"
      />
    </div>
    <div style="height: 68vh">
      <DataTable
        :value="patList"
        :filters.sync="filters"
        filterDisplay="row"
        :loading="loading"
        :paginator="true"
        :rows="20"
        class="p-datatable-sm"
        responsiveLayout="scroll"
        removableSort
        sortMode="single"
        :scrollable="true"
        scrollHeight="flex"
        stripedRows
        :tableStyle="{ 'min-width': '4440px', 'table-layout': 'fixed' }"
        ref="dt"
        :selection.sync="selectedPats"
        :lazy="true"
        :totalRecords="totalRecords"
        @filter="loadLazyData"
        @sort="loadLazyData"
        @page="loadLazyData"
      >
        <template #header>
          <div class="table-header p-d-flex p-jc-between p-ai-center">
            <span
              class="p-d-flex p-jc-between p-ai-center"
              style="flex-grow: 1"
            >
              <Button
                icon="pi pi-plus"
                class="p-button-sm p-button-primary"
                @click="createPat"
                aria-label="Novo PAT"
                v-if="can('managePATs')"
              />
              <span>
                <Button
                  v-if="can('managePATs')"
                  :disabled="!selectedPats || selectedPats.length === 0"
                  icon="pi pi-calendar-plus"
                  label="Criar Intervenção"
                  aria-label="Criar Intervenção"
                  v-tooltip.bottom="'Criar Intervenção'"
                  class="p-button-sm p-button-primary p-mr-1"
                  @click="openCreateIntervention()"
                />
                <Button
                  v-if="can('managePATs')"
                  :disabled="!selectedPats || selectedPats.length === 0"
                  icon="pi pi-sync"
                  label="Alterar Estado"
                  aria-label="Alterar Estado"
                  v-tooltip.bottom="'Alterar Estado'"
                  class="p-button-sm p-button-warning p-mr-1"
                  @click="openChangeStatus()"
                />
                <Button
                  v-if="can('importContactsToGoogleAccount')"
                  :disabled="!selectedPats || selectedPats.length === 0"
                  icon="fa-brands fa-google"
                  label="Importar Contactos conta Google"
                  aria-label="Importar Contactos conta Google"
                  v-tooltip.bottom="'Importar Contactos conta Google'"
                  class="p-button-sm p-button-primary p-mr-1"
                  @click="importContacts()"
                />
                <Button
                  icon="fa-solid fa-file-csv"
                  label="Exportar CSV"
                  aria-label="Exportar CSV"
                  v-tooltip.bottom="'Exportar CSV'"
                  class="p-button-sm p-button-primary p-mr-1"
                  @click="exportCSV($event)"
                />
              </span>
            </span>
            <span class="p-d-flex p-jc-between p-ai-center">
              <span class="p-input-icon-left">
                <i class="pi pi-search" />
                <InputText
                  v-model="search"
                  type="text"
                  placeholder="Pesquisar Pats"
                  class="p-inputtext-sm"
                  @keyup.enter="searchPats"
                />
              </span>
              <span>
                <Button
                  icon="fa-solid fa-search"
                  class="p-button-sm p-button-primary p-ml-1"
                  @click.prevent="searchPats"
                />
              </span>
              <i
                class="pi pi-info-circle text-blue p-ml-2"
                v-tooltip.left="'Pestquisa PATs por id, Cliente ou Equipamento'"
              />
            </span>
          </div>
        </template>
        <template #empty> Nenhum Pat encontrado. </template>
        <template #loading> A carregar... </template>
        <Column
          v-if="can('importContactsToGoogleAccount') || can('managePATs')"
          selectionMode="multiple"
          :headerStyle="{ width: '50px' }"
          :filterHeaderStyle="{ width: '50px' }"
          :bodyStyle="{ width: '50px' }"
        ></Column>
        <Column
          field="id"
          header="Nopat"
          sortable
          filterField="id"
          :headerStyle="{ width: '110px' }"
          :filterHeaderStyle="{ width: '110px' }"
          :bodyStyle="{ width: '110px' }"
        >
          <template #body="slotProps">
            <span class="clickable-item" @click="viewPat(slotProps.data.id)">{{
              slotProps.data.id
            }}</span>
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="oem"
          header="Oem"
          sortable
          filterField="oem"
          :headerStyle="{ width: '130px' }"
          :filterHeaderStyle="{ width: '130px' }"
          :bodyStyle="{ width: '130px' }"
        >
          <template #body="slotProps">
            {{ slotProps.data.oem || "" }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="externalRef"
          header="Vref"
          sortable
          filterField="externalRef"
          :headerStyle="{ width: '130px' }"
          :filterHeaderStyle="{ width: '130px' }"
          :bodyStyle="{ width: '130px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="manufacturerRef"
          header="Ref. Fabricante"
          sortable
          filterField="manufacturerRef"
          :headerStyle="{ width: '170px' }"
          :filterHeaderStyle="{ width: '170px' }"
          :bodyStyle="{ width: '170px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="technicalName"
          header="Técnico"
          sortable
          filterField="technicalName"
          :headerStyle="{ width: '170px' }"
          :filterHeaderStyle="{ width: '170px' }"
          :bodyStyle="{ width: '170px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="interventionDate"
          header="Intervenção"
          sortable
          filterField="interventionDate"
          dataType="date"
          :headerStyle="{ width: '160px' }"
          :filterHeaderStyle="{ width: '160px' }"
          :bodyStyle="{ width: '160px' }"
        >
          <template #body="slotProps">
            {{ slotProps.data.interventionDate }}
            {{ slotProps.data.interventionTime }}
          </template>
          <template #filter="{ filterModel, filterCallback }">
            <v-date-picker
              mode="date"
              v-model="filterModel.value"
              @input="onDateFilterChange($event, filterModel, filterCallback)"
              :masks="{
                input: 'DD-MM-YYYY',
                data: 'DD-MM-YYYY'
              }"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <input
                  class="p-column-filter p-inputtext p-component"
                  :value="inputValue"
                  v-on="inputEvents"
                />
              </template>
            </v-date-picker>
          </template>
        </Column>
        <Column
          field="department"
          header="Centro de assistencia"
          sortable
          filterField="department"
          :headerStyle="{ width: '100px' }"
          :filterHeaderStyle="{ width: '100px' }"
          :bodyStyle="{ width: '100px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="date"
          header="Pdata"
          sortable
          filterField="date"
          dataType="date"
          :headerStyle="{ width: '130px' }"
          :filterHeaderStyle="{ width: '130px' }"
          :bodyStyle="{ width: '130px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <v-date-picker
              mode="date"
              v-model="filterModel.value"
              @input="onDateFilterChange($event, filterModel, filterCallback)"
              :masks="{
                input: 'DD-MM-YYYY',
                data: 'DD-MM-YYYY'
              }"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <input
                  class="p-column-filter p-inputtext p-component"
                  :value="inputValue"
                  v-on="inputEvents"
                />
              </template>
            </v-date-picker>
          </template>
        </Column>
        <Column
          field="numberDays"
          header="TAT"
          sortable
          filterField="numberDays"
          :headerStyle="{ width: '90px' }"
          :filterHeaderStyle="{ width: '90px' }"
          :bodyStyle="{ width: '90px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="equipment.brand"
          header="Marca"
          sortable
          filterField="equipment.brand"
          :headerStyle="{ width: '140px' }"
          :filterHeaderStyle="{ width: '140px' }"
          :bodyStyle="{ width: '140px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="equipment.model"
          header="Maquina"
          sortable
          filterField="equipment.model"
          :headerStyle="{ width: '200px' }"
          :filterHeaderStyle="{ width: '200px' }"
          :bodyStyle="{ width: '200px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column field="status" header="Status" sortable filterField="status">
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="client.name"
          header="Loja"
          sortable
          filterField="client.name"
          :headerStyle="{ width: '220px' }"
          :filterHeaderStyle="{ width: '220px' }"
          :bodyStyle="{ width: '220px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="client.zipCode"
          header="Loja localidade"
          sortable
          filterField="client.zipCode"
          :headerStyle="{ width: '220px' }"
          :filterHeaderStyle="{ width: '220px' }"
          :bodyStyle="{ width: '220px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="client.mobilPhone"
          header="Loja Contacto"
          sortable
          filterField="client.mobilPhone"
          :headerStyle="{ width: '220px' }"
          :filterHeaderStyle="{ width: '220px' }"
          :bodyStyle="{ width: '220px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="entity.name"
          header="Entidade"
          sortable
          filterField="entity.name"
          :headerStyle="{ width: '220px' }"
          :filterHeaderStyle="{ width: '220px' }"
          :bodyStyle="{ width: '220px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="entity.zip_code"
          header="Entidade Localidade"
          sortable
          filterField="entity.zip_code"
          :headerStyle="{ width: '220px' }"
          :filterHeaderStyle="{ width: '220px' }"
          :bodyStyle="{ width: '220px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="entity.tlmvl"
          header="Entidade Contacto"
          sortable
          filterField="entity.tlmvl"
          :headerStyle="{ width: '220px' }"
          :filterHeaderStyle="{ width: '220px' }"
          :bodyStyle="{ width: '220px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="type"
          header="Ptipo"
          sortable
          filterField="type"
          :headerStyle="{ width: '130px' }"
          :filterHeaderStyle="{ width: '130px' }"
          :bodyStyle="{ width: '130px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="publicStatus"
          header="Status Publico"
          sortable
          filterField="publicStatus"
          :headerStyle="{ width: '180px' }"
          :filterHeaderStyle="{ width: '180px' }"
          :bodyStyle="{ width: '180px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          field="supplierOrderRef"
          header="Ref. Enc. Fornecedor"
          sortable
          filterField="supplierOrderRef"
          :headerStyle="{ width: '220px' }"
          :filterHeaderStyle="{ width: '220px' }"
          :bodyStyle="{ width: '220px' }"
        >
          <template #filter="{ filterModel, filterCallback }">
            <DebouncedFilterInput v-model="filterModel.value" :callback="filterCallback" />
          </template>
        </Column>
        <Column
          header="Ações"
          headerClass="p-d-flex p-jc-end"
          bodyClass="p-d-flex p-jc-end"
          v-if="can('managePATs') || can('seePats')"
          :headerStyle="{ width: '110px' }"
          :filterHeaderStyle="{ width: '110px' }"
          :bodyStyle="{ width: '110px' }"
        >
          <template #body="{ data }">
            <Button
              v-if="can('seePats')"
              icon="pi pi-eye"
              class="p-button-rounded p-button-text p-button-success"
              @click="viewPat(data.id)"
            />
            <Button
              v-if="can('managePATs')"
              icon="pi pi-pencil"
              class="p-button-rounded p-button-text"
              @click="editPat(data)"
            />
          </template>
        </Column>
      </DataTable>
    </div>
    <PatModal
      :visible.sync="patDialog"
      :data.sync="patForm"
      @created="patCreated"
      @updated="patUpdated"
      @cancel="closeForm"
    />
    <CreateInterventionDialog
      :visible.sync="interventionDialog"
      :preSelectedPats="interventionPreSelectedPats"
      @created="interventionCreated"
      @cancel="interventionDialog = false"
    />
    <ChangeStatusDialog
      :visible.sync="changeStatusDialog"
      :selectedPats="selectedPats || []"
      @updated="statusUpdated"
      @cancel="changeStatusDialog = false"
    />
  </div>
</template>

<script>
import W4Mstreak from "@/components/W4Mstreak/W4Mstreak.vue";
import PatService from "@/services/pat.service";
import { FilterMatchMode } from "primevue/api";
import PatModal from "@/components/pats/PatModal.vue";
import CreateInterventionDialog from "@/components/support/CreateInterventionDialog.vue";
import ChangeStatusDialog from "@/components/pats/ChangeStatusDialog.vue";
import DebouncedFilterInput from "@/components/elements/DebouncedFilterInput.vue";

export default {
  name: "PATsByStatus",
  components: { W4Mstreak, PatModal, CreateInterventionDialog, ChangeStatusDialog, DebouncedFilterInput },
  data() {
    return {
      loading: false,
      loadingIndicators: false,
      currentStreakIndex: null,
      totalRecords: 0,
      selectedPats: null,
      patList: [],
      streakIndicators: [],
      filters: {
        id: { value: null, matchMode: FilterMatchMode.CONTAINS },
        date: { value: null, matchMode: FilterMatchMode.CONTAINS },
        "equipment.brand": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "equipment.model": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "client.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "client.city": { value: null, matchMode: FilterMatchMode.CONTAINS },
        technicalName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        interventionDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
        interventionTime: { value: null, matchMode: FilterMatchMode.CONTAINS },
        "client.zipCode": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "client.mobilPhone": { value: null, matchMode: FilterMatchMode.CONTAINS },
        publicStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
        status: { value: null, matchMode: FilterMatchMode.CONTAINS },
        oem: { value: null, matchMode: FilterMatchMode.CONTAINS },
        externalRef: { value: null, matchMode: FilterMatchMode.CONTAINS },
        problem: { value: null, matchMode: FilterMatchMode.CONTAINS },
        numberDays: { value: null, matchMode: FilterMatchMode.EQUALS },
        department: { value: null, matchMode: FilterMatchMode.CONTAINS },
        type: { value: null, matchMode: FilterMatchMode.CONTAINS },
        "entity.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "entity.city": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "entity.zip_code": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "entity.tlmvl": { value: null, matchMode: FilterMatchMode.CONTAINS },
        supplierOrderRef: { value: null, matchMode: FilterMatchMode.CONTAINS },
        manufacturerRef: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
      patDialog: false,
      patForm: {},
      interventionDialog: false,
      interventionPreSelectedPats: [],
      changeStatusDialog: false,
      search: "",
    };
  },
  async mounted() {
    await this.loadPATs();
  },
  methods: {
    resetFilters() {
      this.filters = {
        id: { value: null, matchMode: FilterMatchMode.CONTAINS },
        date: { value: null, matchMode: FilterMatchMode.CONTAINS },
        "equipment.brand": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "equipment.model": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "client.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "client.city": { value: null, matchMode: FilterMatchMode.CONTAINS },
        technicalName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        interventionDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
        interventionTime: { value: null, matchMode: FilterMatchMode.CONTAINS },
        "client.zipCode": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "client.mobilPhone": { value: null, matchMode: FilterMatchMode.CONTAINS },
        publicStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
        status: { value: null, matchMode: FilterMatchMode.CONTAINS },
        oem: { value: null, matchMode: FilterMatchMode.CONTAINS },
        externalRef: { value: null, matchMode: FilterMatchMode.CONTAINS },
        problem: { value: null, matchMode: FilterMatchMode.CONTAINS },
        numberDays: { value: null, matchMode: FilterMatchMode.EQUALS },
        department: { value: null, matchMode: FilterMatchMode.CONTAINS },
        type: { value: null, matchMode: FilterMatchMode.CONTAINS },
        "entity.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "entity.city": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "entity.zip_code": { value: null, matchMode: FilterMatchMode.CONTAINS },
        "entity.tlmvl": { value: null, matchMode: FilterMatchMode.CONTAINS },
        supplierOrderRef: { value: null, matchMode: FilterMatchMode.CONTAINS },
        manufacturerRef: { value: null, matchMode: FilterMatchMode.CONTAINS },
      };
    },
    onDateFilterChange(date, filterModel, filterCallback) {
      if (date) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        filterModel.value = `${day}-${month}-${year}`;
      } else {
        filterModel.value = null;
      }

      filterCallback();
    },
    async loadPATs() {
      this.loadingIndicators = true;
      const pats = await PatService.getPatsListStatus();
      this.streakIndicators = pats.map((p) => ({
        title: p.field,
        backgroudColor: p.color || "#e0e0e0",
        textColor: "#000",
        counter: p.pats_count || 0,
        active: false,
        patId: p.id,
      }));
      this.loadingIndicators = false;
    },
    async onPATClick(info) {
      this.search = "";
      this.currentStreakIndex = info.currentStateIndex;
      this.currentStatus = info.currentState;

      this.resetFilters();

      await this.loadLazyData({});
    },
    viewPat(patId) {
      this.$router.push(`/pats/${patId}`);
    },
    closeForm() {
      this.patDialog = false;
      this.patForm = {
        clientId: null,
        equipmentId: null,
      };
      return;
    },
    createPat() {
      this.patForm = {
        clientId: null,
        equipmentId: null,
      };
      this.patDialog = true;
    },
    editPat(pat) {
      this.patForm = { ...pat };
      this.patForm.closed = pat.closed == 1 ? true : false;
      return (this.patDialog = true);
    },
    patCreated() {
      this.closeForm();
      return this.loadPATs();
    },
    patUpdated(pat) {
      const index = this.patList.findIndex((item) => item.id === pat.id);
      if (index !== -1) {
        this.patList.splice(index, 1, pat);
      }
      this.closeForm();
      return this.loadPATs();
    },
    async loadLazyData(event) {
      this.loading = true;

      const page = event.page ? event.page + 1 : 1;

      const params = this.buildQueryParams(page);

      try {
        const response = await PatService.datatableFiltered(params);
        this.patList = response.data;
        this.totalRecords = response.meta.total;
      } catch (error) {
        console.error("Error loading PATs:", error);
        this.$toast.add({
          severity: "error",
          summary: `Erro`,
          detail: `Não foi possível carregar os dados.`,
          life: 3000,
        });
      }

      this.loading = false;
    },
    buildQueryParams(page = 1) {
      const params = new URLSearchParams();

      params.append('page', page);

      if (this.search) {
        params.append('search', this.search);
        return params;
      }

      if (this.currentStreakIndex !== null) {
        const pat = this.streakIndicators[this.currentStreakIndex];
        params.append('status_id', pat.patId);
      }

      for (const [key, filter] of Object.entries(this.filters)) {
        if (filter.value !== null && filter.value !== '') {
          params.append(`filters[${key}]`, filter.value);
        }
      }

      return params;
    },
    async searchPats() {
      if (this.search == null || this.search == "") {
        return;
      }

      this.currentStreakIndex = null;
      this.resetFilters();

      await this.loadLazyData({});

      if (this.patList.length === 0) {
        this.$toast.add({
          severity: "info",
          summary: `Resultado da Pesquisa`,
          detail: `Não existem pats com o termo ${this.search}`,
          life: 3000,
        });
      }
    },
    async exportCSV() {
      this.loading = true;
      try {
        const response = await PatService.exportCSV(this.buildQueryParams());
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `pats_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error("Error exporting CSV:", error);
        this.$toast.add({
          severity: "error",
          summary: `Erro`,
          detail: `Não foi possível exportar os dados.`,
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    async importContacts() {
      let hasErrors = false;
      this.loading = true;
      for (const pat of this.selectedPats) {
        try {
          const response = await PatService.exportContacts(pat.id);
          if (!response) {
            hasErrors = true;
          }
        } catch (error) {
          hasErrors = true;
        }
      }
      this.loading = false;
      if (hasErrors) {
        return this.$toast.add({
          severity: "error",
          summary: `Erro na Importação`,
          detail: `Ocorreu um erro ao importar os contatos.`,
          life: 3000,
        });
      }
      this.$toast.add({
        severity: "success",
        summary: `Importação Efetuada`,
        detail: `A importação dos contatos foi efectuada com sucesso.`,
        life: 3000,
      });
      return (this.selectedPats = null);
    },
    openCreateIntervention() {
      this.interventionPreSelectedPats = this.selectedPats.map((pat) => ({
        patNumber: pat.id,
        patTitle: pat.equipment
          ? `${pat.equipment.brand || ""} ${pat.equipment.model || ""}`.trim()
          : "",
        typename: pat.type || "",
        typeId: pat.typeId || 0,
      }));
      this.interventionDialog = true;
    },
    interventionCreated() {
      this.interventionDialog = false;
      this.selectedPats = null;
    },
    openChangeStatus() {
      if (!this.selectedPats || this.selectedPats.length === 0) return;

      const statuses = [
        ...new Set(this.selectedPats.map((p) => p.status)),
      ];
      if (statuses.length > 1) {
        this.$toast.add({
          severity: "warn",
          summary: "Atenção",
          detail:
            "Todos os PATs selecionados devem ter o mesmo estado para alterar em massa.",
          life: 4000,
        });
        return;
      }

      this.changeStatusDialog = true;
    },
    async statusUpdated() {
      this.changeStatusDialog = false;
      this.selectedPats = null;
      await Promise.all([this.loadPATs(), this.loadLazyData({})]);
    },
  },
};
</script>
<style scoped>
.text-blue {
  color: #007bff;
  cursor: help;
}

/* Force fixed table layout so column widths are honored */
:deep(.p-datatable .p-datatable-table) {
  table-layout: fixed;
}

/* Truncate long content with ellipsis instead of wrapping into multi-line cells */
:deep(.p-datatable-tbody > tr > td) {
  white-space: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Filter row inputs should fill the column width */
:deep(.p-datatable-thead > tr > th .p-column-filter) {
  width: 100%;
  min-width: 0;
}

/* --- Mobile: collapse action buttons to icon-only --- */
@media (max-width: 768px) {
  .table-header {
    flex-wrap: wrap;
    gap: 0.4rem;
  }
  .table-header :deep(.p-button .p-button-label) {
    display: none;
  }
  .table-header :deep(.p-button .p-button-icon) {
    margin: 0;
  }
  .table-header :deep(.p-button) {
    padding: 0.5rem 0.65rem;
    width: auto;
    min-width: 2.4rem;
  }
  .table-header :deep(.p-inputtext) {
    width: 100%;
    min-width: 0;
  }
}
</style>
